import { motion } from "framer-motion";
import { ArrowRight, Users, FileCheck, Shield, Handshake } from "lucide-react";
import { GlowButton } from "@/components/ui/GlowButton";
import { Link } from "react-router-dom";

export const Hero = () => {
  return (
    <section className="relative min-h-screen overflow-hidden pt-16">
      {/* Background */}
      <div className="absolute inset-0 bg-grid-pattern opacity-30" />

      <div className="container relative mx-auto px-4 py-20 lg:py-32">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left content */}
          <div className="space-y-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 rounded-full border border-cream/30 bg-cream/5 px-4 py-2"
            >
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cream opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-cream" />
              </span>
              <span className="text-sm font-medium text-cream">The technical guardians of M&A</span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight"
            >
              Separate <span className="text-cream glow-text">narrative</span>
              <br />
              from reality
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="text-lg text-muted-foreground max-w-lg"
            >
              PROVENANCE is an independent technology due diligence consultancy for investors navigating M&A. Our expert
              team delivers the Technology Reality Score™, cutting through sales narratives to reveal what's truly under
              the hood.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="flex flex-wrap gap-4"
            >
              <Link to="/auth">
                <GlowButton size="lg">
                  Request engagement
                  <ArrowRight className="h-5 w-5" />
                </GlowButton>
              </Link>
              <Link to="/sample-report">
                <GlowButton variant="ghost" size="lg">
                  <FileCheck className="h-5 w-5" />
                  View sample report
                </GlowButton>
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              className="flex flex-wrap items-center gap-4 pt-8 border-t border-border"
            >
              <div className="flex items-center gap-2">
                <Users className="h-5 w-5 text-cream" />
                <span className="text-sm text-muted-foreground">Buyer-side only</span>
              </div>
              <div className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-cream" />
                <span className="text-sm text-muted-foreground">Adversarial by design</span>
              </div>
              <div className="flex items-center gap-2">
                <Handshake className="h-5 w-5 text-cream" />
                <span className="text-sm text-muted-foreground">Independent & confidential</span>
              </div>
            </motion.div>
          </div>

          {/* Right content - Service value proposition */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="relative"
          >
            <div className="relative rounded-2xl border border-border bg-card/50 p-8 backdrop-blur-xl">
              <div className="absolute -inset-1 rounded-2xl bg-gradient-to-r from-cream/10 via-transparent to-cream/10 blur-xl opacity-50" />

              <div className="relative space-y-6">
                <div className="text-center pb-6 border-b border-border">
                  <span className="text-sm text-muted-foreground uppercase tracking-wider">
                    The question every investor should ask
                  </span>
                  <h3 className="font-display text-2xl font-bold mt-3 text-foreground">
                    "Does the technology actually do what we're being told?"
                  </h3>
                </div>

                <div className="space-y-4">
                  {[
                    { label: "Is the 'proprietary AI' real or just a wrapper?", icon: "🔍" },
                    { label: "Will the platform survive the acquisition?", icon: "🏗️" },
                    { label: "What risks are hidden in the dependencies?", icon: "⚠️" },
                    { label: "Is the IP actually owned or contaminated?", icon: "⚖️" },
                  ].map((item) => (
                    <div key={item.label} className="flex items-center gap-3 p-3 rounded-lg bg-secondary/30">
                      <span className="text-xl">{item.icon}</span>
                      <span className="text-sm text-muted-foreground">{item.label}</span>
                    </div>
                  ))}
                </div>

                <div className="pt-4 text-center">
                  <p className="text-sm text-cream font-medium">We answer these questions, before you sign.</p>
                </div>
              </div>
            </div>

            {/* Floating elements */}
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              className="absolute -top-4 -right-4 rounded-lg border border-border bg-card p-3 shadow-lg"
            >
              <div className="flex items-center gap-2">
                <div className="h-2 w-2 rounded-full bg-cream animate-pulse" />
                <span className="text-xs font-medium">Trusted by Corporate Finance, PE & VC firms</span>
              </div>
            </motion.div>

            <motion.div
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
              className="absolute -bottom-4 -left-4 rounded-lg border border-border bg-card p-3 shadow-lg"
            >
              <div className="flex items-center gap-2">
                <FileCheck className="h-4 w-4 text-cream" />
                <span className="text-xs font-medium">Expert-validated reports</span>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
